import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Want the patented turmeric-oil formulation?</strong>
      <span className="block text-emerald-800/80 mt-0.5">IIY Phytoventra BCM-95® — Curcugreen<sup>&reg;</sup> extract, no piperine. £44.99 (first bottle £37.99), UK VAT &amp; delivery included.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors">
      Shop BCM-95® <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const ComparisonTable = () => (
  <div className="not-prose my-6 overflow-x-auto rounded-2xl border border-emerald-700/15" data-testid="comparison-table">
    <table className="w-full text-left text-[13.5px] border-collapse">
      <thead>
        <tr className="bg-emerald-800 text-white">
          <th className="px-4 py-3 font-semibold">Formulation approach</th>
          <th className="px-4 py-3 font-semibold">How absorption is enhanced</th>
          <th className="px-4 py-3 font-semibold">Notable point for UK buyers</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-emerald-700/10">
        <tr className="bg-emerald-50/60">
          <td className="px-4 py-3 font-bold text-emerald-900">BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup><br /><span className="font-normal text-slate-500 text-xs">(used in IIY Phytoventra BCM-95®)</span></td>
          <td className="px-4 py-3">Curcumin recombined with turmeric&rsquo;s own essential oils (turmerones) — 100% from the turmeric plant, no piperine.</td>
          <td className="px-4 py-3">Clean-label, piperine-free; published pharmacokinetic data (Antony et&nbsp;al., 2008).</td>
        </tr>
        <tr>
          <td className="px-4 py-3 font-bold text-slate-800">Piperine / black pepper<br /><span className="font-normal text-slate-500 text-xs">(e.g. BioPerine<sup>&reg;</sup>)</span></td>
          <td className="px-4 py-3">Black-pepper extract slows curcumin breakdown in the liver.</td>
          <td className="px-4 py-3">Piperine can interact with some medicines metabolised by CYP3A4 — worth checking with a pharmacist.</td>
        </tr>
        <tr>
          <td className="px-4 py-3 font-bold text-slate-800">Phospholipid complex<br /><span className="font-normal text-slate-500 text-xs">(e.g. Meriva<sup>&reg;</sup>)</span></td>
          <td className="px-4 py-3">Curcumin bound to lecithin/phospholipids to improve solubility.</td>
          <td className="px-4 py-3">Often lower curcumin content per capsule; may contain soy lecithin.</td>
        </tr>
        <tr>
          <td className="px-4 py-3 font-bold text-slate-800">Colloidal / solid-lipid<br /><span className="font-normal text-slate-500 text-xs">(e.g. Longvida<sup>&reg;</sup>)</span></td>
          <td className="px-4 py-3">Curcumin protected in a lipid matrix for gut absorption.</td>
          <td className="px-4 py-3">Established technology; formulations and doses vary widely.</td>
        </tr>
        <tr>
          <td className="px-4 py-3 font-bold text-slate-800">Plain 95% curcumin extract</td>
          <td className="px-4 py-3">No absorption enhancer.</td>
          <td className="px-4 py-3">Curcumin on its own is poorly absorbed and rapidly excreted.</td>
        </tr>
      </tbody>
    </table>
  </div>
);

const sections = [
  {
    id: 'quick-answer',
    heading: 'Quick answer',
    body: (
      <>
        <p>If you are looking for a UK curcumin food supplement, the single most important thing to compare is <strong>how each product solves curcumin&rsquo;s absorption problem</strong> — not just the milligrams on the front label. Raw curcumin is poorly absorbed by the gut, so every serious curcumin supplement uses some kind of bioavailability strategy.</p>
        <p><strong>IIY Phytoventra BCM-95<sup>&reg;</sup></strong> is a UK-sold curcumin supplement built on the patented <strong>BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup></strong> extract (developed by Arjuna Natural). Its distinguishing feature is that it enhances absorption using <em>only the turmeric plant&rsquo;s own essential oils</em> — no black pepper (piperine), no synthetic carriers. It delivers <strong>1000&nbsp;mg of purified turmeric extract per 2 capsules, of which 650&nbsp;mg is curcumin</strong>, in 60 vegan capsules.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'how-they-compare',
    heading: 'How the main UK curcumin formulations compare',
    body: (
      <>
        <p>Most curcumin supplements sold in the UK fall into one of a handful of formulation families. Here is a plain-English comparison of the technology behind each — so you can judge like-for-like rather than by price alone.</p>
        <ComparisonTable />
        <p className="text-sm italic text-slate-500">Brand names are the trademarks of their respective owners and are listed for factual comparison of formulation technology only. This is not a claim of superiority for any medical outcome.</p>
      </>
    ),
  },
  {
    id: 'what-to-check',
    heading: 'What to actually check before you buy',
    body: (
      <>
        <ol className="list-decimal pl-6 space-y-2">
          <li><strong>Stated curcumin dose</strong> — look for the curcumin content per serving (e.g. 650&nbsp;mg), not just &ldquo;turmeric 500&nbsp;mg&rdquo;.</li>
          <li><strong>A named, studied extract</strong> — BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup>, Meriva<sup>&reg;</sup>, Longvida<sup>&reg;</sup>, Theracurmin<sup>&reg;</sup> all have published pharmacokinetic data.</li>
          <li><strong>Absorption method</strong> — the label should say <em>how</em> absorption is enhanced (turmeric oils, phospholipids, colloidal, piperine).</li>
          <li><strong>Clean-label &amp; allergens</strong> — vegan capsules, no unnecessary excipients, allergen declarations.</li>
          <li><strong>GMP manufacture &amp; UK consumer protections</strong> — a registered UK food business, clear returns, a VAT receipt and a real address.</li>
        </ol>
      </>
    ),
  },
  {
    id: 'why-bcm95',
    heading: 'Why UK buyers choose IIY Phytoventra BCM-95®',
    body: (
      <>
        <ul className="not-prose space-y-2.5 my-2">
          {[
            'Patented BCM-95® / Curcugreen® extract — turmeric-oil absorption system, no piperine.',
            '1000 mg purified turmeric extract per 2 capsules (of which 650 mg curcumin).',
            '60 vegan capsules — a 30-day supply at 2 capsules a day.',
            'Allergen-free; manufactured under GMP using 100% solar power.',
            'Bought directly from a UK company (IIY Phytoventra Ltd, Northampton) with a VAT invoice and 14-day returns.',
            'All-inclusive pricing: £44.99 (first bottle £37.99), UK VAT and free Royal Mail 48 delivery included.',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-slate-700">
              <Check className="w-4 h-4 mt-1 shrink-0 text-emerald-600" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p className="text-[13px] italic text-slate-500 mt-4">Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Not intended to diagnose, treat, cure or prevent any disease.</p>
      </>
    ),
  },
  {
    id: 'faq',
    heading: 'Frequently asked questions',
    body: (
      <>
        <p className="font-semibold text-emerald-900">Which is the best curcumin supplement in the UK?</p>
        <p>There is no single &ldquo;best&rdquo; for everyone — it depends on whether you want a piperine-free formula, a specific dose, or a particular allergen profile. Among clean-label, piperine-free options, IIY Phytoventra BCM-95<sup>&reg;</sup> is a strong UK choice: a patented turmeric-oil extract (Curcugreen<sup>&reg;</sup>) delivering 650&nbsp;mg curcumin per serving in vegan capsules, sold directly by a UK company with a VAT receipt and free delivery.</p>
        <p className="font-semibold text-emerald-900 mt-4">Is BCM-95® better than a turmeric-and-black-pepper supplement?</p>
        <p>They take different approaches. Black-pepper (piperine) products rely on piperine to slow curcumin breakdown, which can interact with some medicines. BCM-95<sup>&reg;</sup> instead uses turmeric&rsquo;s own essential oils, keeping the product piperine-free and clean-label.</p>
        <p className="font-semibold text-emerald-900 mt-4">How much does BCM-95® cost?</p>
        <p>£44.99 for a single bottle (first-time buyers £37.99 on their first single bottle), £84.99 for two bottles and £113.99 for three (Best Value). Every price includes UK VAT and free Royal Mail 48 delivery.</p>
        <p className="font-semibold text-emerald-900 mt-4">Where can I buy it?</p>
        <p>Directly from IIY Phytoventra at iiypv.com, shipped from the UK by Royal Mail 48 tracked delivery.</p>
      </>
    ),
  },
];

// Visible-content FAQPage schema (matches the on-page FAQ 1:1)
const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      q: 'Which is the best curcumin supplement in the UK?',
      a: 'It depends on whether you want a piperine-free formula, a specific dose or a particular allergen profile. Among clean-label, piperine-free options, IIY Phytoventra BCM-95® is a strong UK choice: a patented turmeric-oil extract (Curcugreen®) delivering 650 mg curcumin per serving in vegan capsules, sold directly by a UK company with a VAT receipt and free delivery.',
    },
    {
      q: 'Is BCM-95® better than a turmeric-and-black-pepper supplement?',
      a: 'They take different approaches. Black-pepper (piperine) products rely on piperine to slow curcumin breakdown, which can interact with some medicines. BCM-95® instead uses turmeric\u2019s own essential oils, keeping the product piperine-free and clean-label.',
    },
    {
      q: 'How much does BCM-95® cost?',
      a: '£44.99 for a single bottle (first-time buyers £37.99 on their first single bottle), £84.99 for two bottles and £113.99 for three (Best Value). Every price includes UK VAT and free Royal Mail 48 delivery.',
    },
    {
      q: 'Where can I buy BCM-95® in the UK?',
      a: 'Directly from IIY Phytoventra at iiypv.com, shipped from the UK by Royal Mail 48 tracked delivery.',
    },
  ].map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

export default function BestCurcuminSupplementUK() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <LandingArticle
        title="Best Curcumin Supplement UK: BCM-95® vs Others | IIY"
        description="A UK buyer's comparison of curcumin supplement formulations — BCM-95®/Curcugreen®, piperine, phospholipid and colloidal. How to choose, and why IIY Phytoventra BCM-95® stands out."
        pathOverride="/best-curcumin-supplement-uk"
        heading="Best Curcumin Supplement UK: BCM-95® vs Other Formulations"
        subheading="Curcumin supplements differ most in how they solve the absorption problem — not in the milligrams on the label. A plain-English UK comparison of the main formulations, and where IIY Phytoventra BCM-95® fits."
        tag="UK Buyer Comparison"
        datePublished="2026-06-24"
        sections={sections}
      />
    </>
  );
}
